import type { User } from '@supabase/supabase-js';
import { supabase } from './supabase';

/**
 * Construit un export JSON complet des données personnelles de l'utilisateur
 * — conforme au droit d'accès / portabilité (Loi 25 art. 27, RGPD art. 15 & 20).
 *
 * Inclut :
 *   - identité auth (id, email, metadata, dates)
 *   - toutes les organisations dont il est membre (+ rôle)
 *   - tous les générateurs de ces organisations
 *   - toutes les soumissions (leads) reçues
 *
 * Renvoie un objet sérialisable en JSON. L'appelant se charge de déclencher
 * le téléchargement via un Blob.
 */
export interface UserDataExport {
    exported_at: string;
    format_version: '1.0';
    user: {
        id: string;
        email: string | null;
        created_at: string | undefined;
        last_sign_in_at: string | undefined;
        display_name: string | null;
        avatar_url: string | null;
        provider: string | undefined;
        metadata: Record<string, unknown>;
    };
    organizations: Array<{
        role: string;
        organization: Record<string, unknown>;
        generators: Array<Record<string, unknown>>;
        submissions: Array<Record<string, unknown>>;
    }>;
}

export async function buildUserDataExport(rawUser: User): Promise<UserDataExport> {
    const meta = rawUser.user_metadata ?? {};

    // 1. Memberships + orgs
    const { data: memberships, error: mErr } = await supabase
        .from('organization_members')
        .select('role, organizations:organization_id(*)')
        .eq('user_id', rawUser.id);

    if (mErr) throw mErr;

    const orgsData: UserDataExport['organizations'] = [];

    for (const row of memberships ?? []) {
        const rawOrg = row.organizations as unknown as Record<string, unknown> | Record<string, unknown>[] | null;
        const org = (Array.isArray(rawOrg) ? rawOrg[0] : rawOrg) as Record<string, unknown> | null;
        if (!org) continue;

        const orgId = org.id as string;

        // 2. Générateurs de l'org
        const { data: generators, error: gErr } = await supabase
            .from('generators')
            .select('*')
            .eq('organization_id', orgId);
        if (gErr) throw gErr;

        // 3. Soumissions de l'org
        const { data: submissions, error: sErr } = await supabase
            .from('submissions')
            .select('*')
            .eq('organization_id', orgId);
        if (sErr) throw sErr;

        orgsData.push({
            role: row.role as string,
            organization: org,
            generators: generators ?? [],
            submissions: submissions ?? [],
        });
    }

    return {
        exported_at: new Date().toISOString(),
        format_version: '1.0',
        user: {
            id: rawUser.id,
            email: rawUser.email ?? null,
            created_at: rawUser.created_at,
            last_sign_in_at: rawUser.last_sign_in_at,
            display_name:
                (meta.display_name as string | undefined) ??
                (meta.full_name as string | undefined) ??
                (meta.name as string | undefined) ??
                null,
            avatar_url:
                (meta.avatar_url as string | undefined) ??
                (meta.picture as string | undefined) ??
                null,
            provider: rawUser.app_metadata?.provider,
            metadata: meta as Record<string, unknown>,
        },
        organizations: orgsData,
    };
}

/**
 * Télécharge le JSON dans le navigateur sous le nom `quotio-data-YYYYMMDD.json`.
 */
export function downloadJsonFile(data: unknown, filename: string) {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function buildExportFilename(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `quotio-data-${yyyy}${mm}${dd}.json`;
}
